import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { getStoredUTM } from "../lib/utm";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Zap, LineChart, Shield, MousePointerClick, Target, Layers } from "lucide-react";

export default function Landing() {
  const [banking, setBanking] = useState([]);
  const [ecom, setEcom] = useState([]);
  const [utm, setUtm] = useState({});

  useEffect(() => {
    setUtm(getStoredUTM());
    Promise.all([
      // Banking section shows all lenders including Loan Express exclusives
      api.get("/products", { params: { type: "banking", channel: "all" } }),
      api.get("/products", { params: { type: "ecom" } }),
    ]).then(([b, e]) => {
      setBanking(b.data.slice(0, 3));
      setEcom(e.data.slice(0, 3));
    });
  }, []);

  const hasUtm = Object.keys(utm).length > 0;

  return (
    <div className="grain">
      {/* HERO */}
      <section className="relative gradient-mesh">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-24 md:pt-24 md:pb-32">
          <div className="flex items-center gap-3 mb-8">
            <Badge className="badge-mono bg-black text-white hover:bg-black">MP · 2026</Badge>
            <span className="badge-mono text-neutral-600">One marketplace · Two verticals · Full attribution</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-4xl">
            Launch <span className="font-serif-accent text-[#0055FF]">banking</span> & ecom offers
            <br />
            with <span className="font-serif-accent">attribution</span> that actually works.
          </h1>
          <p className="mt-6 text-lg text-neutral-600 max-w-2xl leading-relaxed">
            A marketplace built for growth teams. Publish credit cards, loans, insurance and ecommerce products —
            capture UTM, track every click and conversion, and give partners a lead pipeline they can trust.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/marketplace"><Button data-testid="hero-browse-btn" className="h-12 px-6 bg-black hover:bg-[#0055FF] text-white text-base font-medium cta-cursor">
              Browse marketplace <ArrowRight className="w-4 h-4 ml-2" />
            </Button></Link>
            <Link to="/login"><Button data-testid="hero-partner-btn" variant="outline" className="h-12 px-6 border-black/15 text-base">
              Partner sign in
            </Button></Link>
            <div className="flex items-center gap-2 pl-3 text-xs text-neutral-500 font-mono-accent">
              <span className={`inline-block w-2 h-2 rounded-full ${hasUtm ? "bg-[#00A36C] animate-pulse" : "bg-neutral-300"}`} />
              {hasUtm ? `Tracking · ${utm.utm_source || "direct"}/${utm.utm_medium || "-"}` : "session · direct"}
            </div>
          </div>

          {/* Bento stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { k: "12.4M", v: "Clicks tracked" },
              { k: "87k", v: "Leads captured" },
              { k: "241", v: "Live products" },
              { k: "38", v: "Bank + ecom partners" },
            ].map((s) => (
              <div key={s.v} className="bg-white border border-black/8 rounded-lg p-5">
                <div className="font-display text-3xl font-bold tracking-tight">{s.k}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mt-2">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Marquee ticker */}
        <div className="border-y border-black/8 bg-white/40 overflow-hidden">
          <div className="marquee-track flex items-center gap-12 py-4 whitespace-nowrap">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-center gap-12">
                {["Nova Bank", "Zenith Financial", "Aegis Life", "Orbit Wearables", "Nimbus Audio", "Kite Athletic", "Meridian Capital", "Halcyon Cards"].map((n) => (
                  <span key={n + i} className="font-display text-xl tracking-tight text-neutral-500">
                    {n} <span className="text-neutral-300">◇</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-end mb-14">
          <div>
            <div className="badge-mono text-neutral-500 mb-3">Section 01 / Attribution</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-none">
              Every click, session and lead — <span className="font-serif-accent">connected</span>.
            </h2>
          </div>
          <p className="text-neutral-600 leading-relaxed text-lg">
            UTM parameters follow the user from landing → product → application. First-touch attribution is stored
            per session, and admins see a live funnel for every campaign.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Target, title: "UTM capture", copy: "utm_source, medium, campaign, term, content parsed on landing and stored per session." },
            { icon: MousePointerClick, title: "Click tracking", copy: "Every CTA fires a click event with IP, UA, referrer and session id." },
            { icon: LineChart, title: "Conversion attribution", copy: "Lead form submissions tie back to the original UTM — CTR by source, campaign, product." },
          ].map((f) => (
            <div key={f.title} className="bg-white border border-black/8 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <f.icon className="w-6 h-6 text-[#0055FF]" strokeWidth={1.8} />
              <div className="mt-6 font-display text-xl font-semibold tracking-tight">{f.title}</div>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{f.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BANKING FEATURED */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="badge-mono text-neutral-500 mb-2">Section 02 / Banking</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Featured banking products</h2>
          </div>
          <Link to="/marketplace?type=banking" data-testid="see-all-banking" className="text-sm font-medium hover:text-[#0055FF]">See all →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {banking.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* ECOM FEATURED */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="badge-mono text-neutral-500 mb-2">Section 03 / Commerce</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Trending on the shop</h2>
          </div>
          <Link to="/marketplace?type=ecom" data-testid="see-all-ecom" className="text-sm font-medium hover:text-[#0055FF]">See all →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {ecom.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* CTA FOR PARTNERS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="bg-black text-white rounded-2xl p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="badge-mono text-neutral-400 mb-4">Partner program</div>
            <h3 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-none">
              List your product. <br /><span className="font-serif-accent text-[#FFB800]">Watch conversions.</span>
            </h3>
            <p className="mt-5 text-neutral-300 leading-relaxed">
              Banks, insurers and DTC brands — publish offers, receive live leads and get a real-time dashboard
              of clicks, campaigns and CTR.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/login"><Button data-testid="partner-cta-btn" className="h-12 px-6 bg-white hover:bg-[#FFB800] hover:text-black text-black font-medium">
                Become a partner
              </Button></Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Zap, l: "Live", v: "Real-time leads" },
              { icon: Shield, l: "Secure", v: "Role-based access" },
              { icon: Layers, l: "Flexible", v: "3 lead capture modes" },
              { icon: LineChart, l: "Insights", v: "CTR by campaign" },
            ].map((s) => (
              <div key={s.v} className="bg-white/5 border border-white/10 rounded-lg p-5">
                <s.icon className="w-5 h-5 text-[#FFB800]" strokeWidth={1.8} />
                <div className="text-xs uppercase tracking-[0.15em] text-neutral-400 mt-4">{s.l}</div>
                <div className="font-display text-lg font-semibold tracking-tight">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
